import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, writeBatch, getDoc } from '../firebase';
import { db } from '../firebase';
import { FileUp, Link2, Plus, Calendar } from 'lucide-react';
import { cn } from '../lib/utils';
import { notify } from '../services/notify';
import { SearchableSelect } from './ui/SearchableSelect';
import { Item } from '../types';

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');

export default function LandedCostsView({ orgId, language }: { orgId: string, language: string }) {
  const [activeTab, setActiveTab] = useState<'submission' | 'allocation'>('submission');

  // Common data
  const [accounts, setAccounts] = useState<any[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [poItems, setPoItems] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);

  // Submission Form
  const [subDate, setSubDate] = useState(new Date().toISOString().split('T')[0]);
  const [subRef, setSubRef] = useState('');
  const [subAmount, setSubAmount] = useState(0);
  const [subFromAccount, setSubFromAccount] = useState('');
  const [subInterAccount, setSubInterAccount] = useState('');

  // Allocation Form
  const [allocSubId, setAllocSubId] = useState('');
  const [allocPOId, setAllocPOId] = useState('');
  const [allocInventoryAccountId, setAllocInventoryAccountId] = useState('');
  const [allocMethod, setAllocMethod] = useState<'Value' | 'Quantity'>('Value');

  useEffect(() => {
    fetchData();
  }, [orgId, activeTab]);

  const fetchData = async () => {
    try {
      const localToken = getLocalAuthToken();
      if (localToken) {
        const [accountsResponse, purchaseOrdersResponse, submissionsResponse] = await Promise.all([
          fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: { Authorization: `Bearer ${localToken}` } }),
          fetch(`/api/organizations/${orgId}/documents?types=purchase_order`, { headers: { Authorization: `Bearer ${localToken}` } }),
          fetch(`/api/organizations/${orgId}/landed-cost-submissions`, { headers: { Authorization: `Bearer ${localToken}` } })
        ]);
        if (!accountsResponse.ok || !purchaseOrdersResponse.ok || !submissionsResponse.ok) throw new Error('Could not load landed cost data');
        const [localAccounts, localPurchaseOrders, localSubmissions] = await Promise.all([
          accountsResponse.json(), purchaseOrdersResponse.json(), submissionsResponse.json()
        ]);
        setAccounts(Array.isArray(localAccounts) ? localAccounts : []);
        setPurchaseOrders(Array.isArray(localPurchaseOrders) ? localPurchaseOrders : []);
        setPoItems((Array.isArray(localPurchaseOrders) ? localPurchaseOrders : []).flatMap(purchaseOrder =>
          (purchaseOrder.items || []).map((item: any) => ({ ...item, documentId: purchaseOrder.id }))
        ));
        setSubmissions((Array.isArray(localSubmissions) ? localSubmissions : []).filter((submission: any) => submission.status === 'Available'));
        return;
      }
      const [accSnap, poSnap, docsSnap, subSnap] = await Promise.all([
        getDocs(query(collection(db, 'chart_of_accounts'), where('orgId', '==', orgId))),
        getDocs(query(collection(db, 'documents'), where('orgId', '==', orgId), where('type', '==', 'purchase_order'))),
        getDocs(query(collection(db, 'documents_items'), where('orgId', '==', orgId))),
        getDocs(query(collection(db, 'landed_cost_submissions'), where('orgId', '==', orgId)))
      ]);

      setAccounts(accSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPurchaseOrders(poSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPoItems(docsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      const loadedSubs = subSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter((s: any) => s.status === 'Available' || !s.status);
      setSubmissions(loadedSubs);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSubSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subRef || !subAmount || !subFromAccount || !subInterAccount) {
      notify.error('Please fill all fields');
      return;
    }

    try {
      const localToken = getLocalAuthToken();
      if (localToken) {
        const response = await fetch(`/api/organizations/${orgId}/landed-cost-submissions`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: subDate, reference: subRef, amount: subAmount, fromAccountId: subFromAccount, interAccountId: subInterAccount })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || 'Could not create landed cost submission');
        notify.confirm('Landed Cost Submission created successfully');
        setSubRef(''); setSubAmount(0); setSubFromAccount(''); setSubInterAccount('');
        await fetchData(); setActiveTab('allocation');
        return;
      }
      // 1. Create a Submission record
      const subDocRef = await addDoc(collection(db, 'landed_cost_submissions'), {
        orgId,
        date: subDate,
        reference: subRef,
        amount: subAmount,
        fromAccountId: subFromAccount,
        interAccountId: subInterAccount,
        status: 'Available',
        createdAt: new Date().toISOString()
      });

      // 2. Create Journal Entry
      const fromAcc = accounts.find(a => a.id === subFromAccount);
      const interAcc = accounts.find(a => a.id === subInterAccount);

      await addDoc(collection(db, 'manual_journals'), {
        orgId,
        journalNumber: `LC-SUB-${Date.now().toString().slice(-4)}`,
        date: subDate,
        narration: `Landed Cost Submission: ${subRef}`,
        status: 'Posted',
        totalDebit: subAmount,
        totalCredit: subAmount,
        createdAt: new Date().toISOString(),
        lines: [
          {
            accountId: interAcc.id,
            accountName: interAcc.name,
            description: subRef,
            debit: subAmount,
            credit: 0
          },
          {
            accountId: fromAcc.id,
            accountName: fromAcc.name,
            description: subRef,
            debit: 0,
            credit: subAmount
          }
        ]
      });

      notify.confirm('Landed Cost Submission created successfully');
      setSubRef('');
      setSubAmount(0);
      setSubFromAccount('');
      setSubInterAccount('');
      fetchData(); // Refresh submissions list
      setActiveTab('allocation');
    } catch (err) {
      console.error(err);
      notify.error('Error creating submission');
    }
  };

  const handleAllocSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allocSubId || !allocPOId || !allocMethod || !allocInventoryAccountId) {
      notify.error('Please select submission, PO, inventory account, and allocation method');
      return;
    }

    const sub = submissions.find(s => s.id === allocSubId);
    if (!sub) return;
    
    // Find PO items
    const items = poItems.filter(i => i.documentId === allocPOId);
    if (items.length === 0) {
      notify.error('No items found in selected PO');
      return;
    }

    try {
      const localToken = getLocalAuthToken();
      if (localToken) {
        const response = await fetch(`/api/organizations/${orgId}/landed-cost-submissions/${sub.id}/allocate`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ purchaseOrderId: allocPOId, inventoryAccountId: allocInventoryAccountId, method: allocMethod })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || 'Could not allocate landed costs');
        notify.confirm('Landed Cost Allocated Successfully');
        setAllocSubId(''); setAllocPOId(''); setAllocInventoryAccountId('');
        await fetchData();
        return;
      }
      const batch = writeBatch(db);

      // Simple allocation logic
      let totalMetric = 0;
      if (allocMethod === 'Value') {
        totalMetric = items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
      } else {
        totalMetric = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      }

      if (totalMetric === 0) {
        notify.error('Cannot allocate: Total value/quantity is zero');
        return;
      }

      // Update Sub Status
      batch.update(doc(db, 'landed_cost_submissions', sub.id), { status: 'Allocated' });

      // Create LC Allocation Record
      const allocRef = doc(collection(db, 'landed_cost_allocations'));
      batch.set(allocRef, {
        orgId,
        submissionId: sub.id,
        purchaseOrderId: allocPOId,
        inventoryAccountId: allocInventoryAccountId,
        method: allocMethod,
        date: new Date().toISOString()
      });

      // Journal Lines will Debit inventory account and Credit intermediary account
      const inventoryAcc = accounts.find(a => a.id === allocInventoryAccountId);
      const interAcc = accounts.find(a => a.id === sub.interAccountId);

      const journalLines = [
        {
          accountId: sub.interAccountId,
          accountName: interAcc?.name || 'Intermediary Account',
          description: `Allocation of ${sub.reference} to PO (Credit Intermediary)`,
          debit: 0,
          credit: sub.amount
        },
        {
          accountId: inventoryAcc?.id,
          accountName: inventoryAcc?.name || 'Inventory Account',
          description: `Allocation of ${sub.reference} to inventory (Debit Inventory)`,
          debit: sub.amount,
          credit: 0
        }
      ];

      // Update actual item costs if those items exist in inventory
      for (const item of items) {
        let fraction = 0;
        if (allocMethod === 'Value') {
          fraction = ((item.quantity || 0) * (item.unitPrice || 0)) / totalMetric;
        } else {
          fraction = (item.quantity || 0) / totalMetric;
        }

        const allocatedCost = sub.amount * fraction;
        const additionalCostPerUnit = allocatedCost / (item.quantity || 1);

        // Fetch item and update its stock records with the new cost
        if (item.itemId) {
          const itemDoc = await getDoc(doc(db, 'items', item.itemId));
          if (itemDoc.exists()) {
            const itemData = itemDoc.data() as Item;
            const updatedRecords = (itemData.stockRecords || []).map(record => {
              // Only update records that match the PO (if we track it) or just update recent ones
              // Here we just apply it proportionally to current stock if we don't have exact link
              return {
                ...record,
                costPrice: (record.costPrice || itemData.costPrice || 0) + additionalCostPerUnit
              };
            });
            batch.update(doc(db, 'items', item.itemId), {
              stockRecords: updatedRecords,
              costPrice: (itemData.costPrice || 0) + additionalCostPerUnit
            });
          }
        }
      }

      const journalRef = doc(collection(db, 'manual_journals'));
      batch.set(journalRef, {
        orgId,
        journalNumber: `LC-ALC-${Date.now().toString().slice(-4)}`,
        date: new Date().toISOString(),
        narration: `Landed Cost Allocation: ${sub.reference}`,
        status: 'Posted',
        totalDebit: sub.amount,
        totalCredit: sub.amount,
        createdAt: new Date().toISOString(),
        lines: journalLines
      });

      await batch.commit();
      notify.confirm('Landed Cost Allocated Successfully');
      setAllocSubId('');
      setAllocPOId('');
      setAllocInventoryAccountId('');
      fetchData();

    } catch (err) {
      console.error(err);
      notify.error('Error allocating landed costs');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Landed Costs</h1>
          <p className="text-sm opacity-60">Submit costs and allocate them to Purchase Orders.</p>
        </div>
      </div>

      <div className="flex border-b border-[#141414]/10">
        <button
          onClick={() => setActiveTab('submission')}
          className={cn(
            "px-6 py-3 font-bold text-sm tracking-widest uppercase transition-all border-b-2",
            activeTab === 'submission' ? "border-[#141414]" : "border-transparent opacity-50 hover:opacity-100"
          )}
        >
          Submission
        </button>
        <button
          onClick={() => setActiveTab('allocation')}
          className={cn(
            "px-6 py-3 font-bold text-sm tracking-widest uppercase transition-all border-b-2",
            activeTab === 'allocation' ? "border-[#141414]" : "border-transparent opacity-50 hover:opacity-100"
          )}
        >
          Allocation
        </button>
      </div>

      <div className="bg-white/50 border border-[#141414]/10 rounded-2xl p-6">
        {activeTab === 'submission' ? (
          <form onSubmit={handleSubSubmit} className="space-y-6 max-w-2xl">
            <h2 className="text-lg font-bold font-mono border-b border-[#141414]/10 pb-4 mb-4 flex items-center gap-2">
              <FileUp className="w-5 h-5" /> New Cost Submission
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Date *</label>
                <input
                  required
                  type="date"
                  className="w-full bg-white/50 border border-[#141414]/20 p-3 rounded-xl outline-none focus:border-[#141414] transition-all text-sm font-bold"
                  value={subDate}
                  onChange={(e) => setSubDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Reference/Name *</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. Freight Cost 001"
                  className="w-full bg-white/50 border border-[#141414]/20 p-3 rounded-xl outline-none focus:border-[#141414] transition-all text-sm font-bold"
                  value={subRef}
                  onChange={(e) => setSubRef(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Total Amount *</label>
              <input
                required
                type="number"
                step="0.01"
                min="0.01"
                className="w-full bg-white/50 border border-[#141414]/20 p-3 rounded-xl outline-none focus:border-[#141414] transition-all text-sm font-mono font-bold"
                value={subAmount || ''}
                onChange={(e) => setSubAmount(parseFloat(e.target.value))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">From Account (Credit) *</label>
                <SearchableSelect
                  options={accounts.filter(a => a.type === 'Bank' || a.type === 'Cash' || a.type === 'Accounts Payable').map(acc => ({
                    value: acc.id,
                    label: acc.name,
                    subLabel: acc.code
                  }))}
                  value={subFromAccount}
                  onChange={(e) => setSubFromAccount(e.target.value)}
                  placeholder="Select source account..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Intermediary Account (Debit) *</label>
                <SearchableSelect
                  options={accounts.map(acc => ({
                    value: acc.id,
                    label: acc.name,
                    subLabel: acc.code
                  }))}
                  value={subInterAccount}
                  onChange={(e) => setSubInterAccount(e.target.value)}
                  placeholder="Select intermediary account..."
                />
              </div>
            </div>

            <div className="pt-4">
              <button 
                type="submit"
                className="bg-[#141414] text-white px-6 py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] hover:bg-[#141414]/80 transition-colors w-full"
              >
                Submit Cost & Create Journal
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleAllocSubmit} className="space-y-6 max-w-2xl">
             <h2 className="text-lg font-bold font-mono border-b border-[#141414]/10 pb-4 mb-4 flex items-center gap-2">
              <Link2 className="w-5 h-5" /> Allocate Cost to PO
            </h2>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Available Cost Submissions *</label>
              <SearchableSelect
                options={submissions.map(sub => ({
                  value: sub.id,
                  label: sub.reference,
                  subLabel: `$${sub.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })} (${sub.date})`
                }))}
                value={allocSubId}
                onChange={(e) => setAllocSubId(e.target.value)}
                placeholder="Select submission to allocate..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Target Purchase Order *</label>
              <SearchableSelect
                options={purchaseOrders.map(po => ({
                  value: po.id,
                  label: po.reference || po.id,
                  subLabel: `$${po.totalAmount}`
                }))}
                value={allocPOId}
                onChange={(e) => setAllocPOId(e.target.value)}
                placeholder="Select Purchase Order..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Inventory Account (Debit) *</label>
              <SearchableSelect
                options={accounts.filter(a => a.type === 'Inventory' || a.name.toLowerCase().includes('inventory')).map(acc => ({
                  value: acc.id,
                  label: acc.name,
                  subLabel: acc.code
                }))}
                value={allocInventoryAccountId}
                onChange={(e) => setAllocInventoryAccountId(e.target.value)}
                placeholder="Select Inventory Account..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Allocation Method *</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setAllocMethod('Value')}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all font-bold text-center",
                    allocMethod === 'Value' ? "border-[#141414] bg-[#141414] text-white" : "border-[#141414]/20 bg-white/50 hover:border-[#141414]/50"
                  )}
                >
                  By Value
                </button>
                <button
                  type="button"
                  onClick={() => setAllocMethod('Quantity')}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all font-bold text-center",
                    allocMethod === 'Quantity' ? "border-[#141414] bg-[#141414] text-white" : "border-[#141414]/20 bg-white/50 hover:border-[#141414]/50"
                  )}
                >
                  By Quantity
                </button>
              </div>
            </div>

            <div className="pt-4">
              <button 
                type="submit"
                className="bg-[#141414] text-white px-6 py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] hover:bg-[#141414]/80 transition-colors w-full"
              >
                Allocate & Post Journal
              </button>
            </div>

          </form>
        )}
      </div>

    </div>
  );
}
